/**
 * CODEX admin Content Manager.
 * Lets Pastor Obed add new Hebrew/Greek words to the library at scale.
 */
import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Plus, Trash2, Sparkles, X } from 'lucide-react';

const BASE = process.env.REACT_APP_BACKEND_URL;
const tok = () => localStorage.getItem('access_token');
const auth = () => ({ Authorization: `Bearer ${tok()}` });

const blankLayer = { prompt: '', options: ['', '', '', ''], correct_index: 0, explanation: '' };

export default function CodexContentManager() {
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [composer, setComposer] = useState(null);
  const [filter, setFilter] = useState('all');

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${BASE}/api/codex/admin/words`, { headers: auth() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setItems(d.items);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const blank = () => ({
    language: 'hebrew',
    transliteration: '',
    script: '',
    strongs: '',
    short_meaning: '',
    pronunciation_hint: '',
    layer_literal: { ...blankLayer, options: ['', '', '', ''] },
    layer_cultural: { ...blankLayer, options: ['', '', '', ''] },
    layer_prophetic: { ...blankLayer, options: ['', '', '', ''] },
    reflection_prompt: '',
  });

  const submit = async () => {
    try {
      const r = await fetch(`${BASE}/api/codex/admin/words`, {
        method: 'POST',
        headers: { ...auth(), 'Content-Type': 'application/json' },
        body: JSON.stringify(composer),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.detail || `HTTP ${r.status}`);
      }
      setComposer(null);
      await load();
    } catch (e) {
      alert(e.message);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this word permanently?')) return;
    const r = await fetch(`${BASE}/api/codex/admin/words/${id}`, { method: 'DELETE', headers: auth() });
    if (!r.ok) {
      alert((await r.json().catch(() => ({}))).detail || 'Delete failed');
      return;
    }
    await load();
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', opacity: 0.7 }}>Loading library…</div>;
  if (error) return <div style={{ padding: 40, color: 'salmon' }}>{error}</div>;

  const filtered = items.filter((x) => filter === 'all' || (filter === 'seasonal' ? x.season : x.language === filter));

  return (
    <div data-testid="codex-content-manager" style={{ paddingTop: 8 }}>
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} style={{ marginBottom: 18, display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <div className="codex-eyebrow">CONTENT MANAGER · ADMIN</div>
          <h1 className="codex-h1" style={{ marginTop: 6 }}>Word Library</h1>
          <p style={{ opacity: 0.75, marginTop: 6 }}>{items.length} words · scale toward 365 days at your own pace.</p>
        </div>
        <button onClick={() => setComposer(blank())} className="codex-btn codex-btn-primary" data-testid="codex-content-new">
          <Plus size={14} /> Add Word
        </button>
      </motion.div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {['all', 'hebrew', 'greek', 'seasonal'].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className="codex-btn codex-btn-ghost"
            style={{
              padding: '6px 12px',
              fontSize: 11,
              background: filter === f ? 'rgba(212,175,55,0.18)' : undefined,
              borderColor: filter === f ? 'rgba(212,175,55,0.6)' : undefined,
            }}
          >
            {f}
          </button>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
        {filtered.map((w) => (
          <div key={w.id} className="codex-card" style={{ padding: 14, position: 'relative' }}>
            <button
              onClick={() => remove(w.id)}
              className="codex-btn codex-btn-ghost"
              style={{ position: 'absolute', top: 8, right: 8, padding: '4px 6px', fontSize: 10, borderColor: 'rgba(239,68,68,0.3)', color: '#f87171' }}
              data-testid={`codex-content-delete-${w.id}`}
            ><Trash2 size={11} /></button>
            <div className="codex-eyebrow" style={{ color: w.season ? '#22c55e' : (w.language === 'hebrew' ? 'var(--codex-gold-light)' : '#a78bfa') }}>
              {w.season ? `SEASONAL · ${w.season}` : w.language}
            </div>
            <div className={w.language === 'hebrew' ? 'codex-hebrew' : 'codex-greek'} style={{ fontSize: 28, color: 'var(--codex-gold-light)', marginTop: 6 }}>{w.script}</div>
            <div className="codex-display" style={{ fontSize: 16, marginTop: 4 }}>{w.transliteration}</div>
            <div style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}>{w.short_meaning}</div>
          </div>
        ))}
      </div>

      {composer && <ComposerModal composer={composer} setComposer={setComposer} onSave={submit} />}
    </div>
  );
}

function ComposerModal({ composer, setComposer, onSave }) {
  const setField = (k, v) => setComposer({ ...composer, [k]: v });
  const setLayer = (key, layer) => setComposer({ ...composer, [key]: layer });

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      onClick={() => setComposer(null)}
      style={{ position: 'fixed', inset: 0, background: 'rgba(11,10,22,0.88)', backdropFilter: 'blur(8px)', zIndex: 70, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 20, overflowY: 'auto' }}
    >
      <motion.div
        initial={{ scale: 0.96, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        onClick={(e) => e.stopPropagation()}
        className="codex-card-gold codex-card"
        style={{ padding: 26, maxWidth: 760, width: '100%', marginTop: 20, marginBottom: 20 }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <h2 className="codex-h2">Add a New Word</h2>
          <button onClick={() => setComposer(null)} className="codex-btn codex-btn-ghost" style={{ padding: '6px 10px' }}><X size={14} /></button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
          <select value={composer.language} onChange={(e) => setField('language', e.target.value)} className="codex-input">
            <option value="hebrew">Hebrew</option>
            <option value="greek">Greek</option>
          </select>
          <input className="codex-input" placeholder="Strong's (optional, e.g. H7965)" value={composer.strongs} onChange={(e) => setField('strongs', e.target.value)} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
          <input className="codex-input" placeholder="Transliteration (e.g. Shalom)" value={composer.transliteration} onChange={(e) => setField('transliteration', e.target.value)} />
          <input className="codex-input" placeholder="Script (native)" value={composer.script} onChange={(e) => setField('script', e.target.value)} />
        </div>
        <input className="codex-input" placeholder="Short meaning" value={composer.short_meaning} onChange={(e) => setField('short_meaning', e.target.value)} style={{ marginBottom: 12 }} />
        <input className="codex-input" placeholder="Pronunciation hint (e.g. shah-LOHM)" value={composer.pronunciation_hint} onChange={(e) => setField('pronunciation_hint', e.target.value)} style={{ marginBottom: 12 }} />

        {[
          { key: 'layer_literal', label: 'Layer 1 — Literal' },
          { key: 'layer_cultural', label: 'Layer 2 — Cultural' },
          { key: 'layer_prophetic', label: 'Layer 3 — Prophetic' },
        ].map(({ key, label }) => (
          <div key={key} className="codex-card" style={{ padding: 14, marginBottom: 10, background: 'rgba(0,0,0,0.2)' }}>
            <div className="codex-eyebrow" style={{ marginBottom: 8 }}>{label}</div>
            <input className="codex-input" placeholder="Quiz prompt" value={composer[key].prompt} onChange={(e) => setLayer(key, { ...composer[key], prompt: e.target.value })} style={{ marginBottom: 8 }} />
            {[0, 1, 2, 3].map((i) => (
              <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'center' }}>
                <input
                  type="radio"
                  checked={composer[key].correct_index === i}
                  onChange={() => setLayer(key, { ...composer[key], correct_index: i })}
                  title="Correct answer"
                />
                <input
                  className="codex-input"
                  placeholder={`Option ${String.fromCharCode(65 + i)}`}
                  value={composer[key].options[i]}
                  onChange={(e) => {
                    const opts = [...composer[key].options];
                    opts[i] = e.target.value;
                    setLayer(key, { ...composer[key], options: opts });
                  }}
                  style={{ flex: 1 }}
                />
              </div>
            ))}
            <textarea className="codex-input" rows={2} placeholder="Explanation shown after submit" value={composer[key].explanation} onChange={(e) => setLayer(key, { ...composer[key], explanation: e.target.value })} />
          </div>
        ))}

        <textarea className="codex-input" rows={2} placeholder="Reflection prompt" value={composer.reflection_prompt} onChange={(e) => setField('reflection_prompt', e.target.value)} style={{ marginBottom: 14 }} />

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button onClick={() => setComposer(null)} className="codex-btn codex-btn-ghost">Cancel</button>
          <button
            onClick={onSave}
            disabled={!composer.transliteration || !composer.script || !composer.short_meaning}
            className="codex-btn codex-btn-primary"
            style={{ opacity: (!composer.transliteration || !composer.script || !composer.short_meaning) ? 0.4 : 1 }}
            data-testid="codex-content-save"
          >
            <Sparkles size={14} /> Save Word
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
